import React, { useState } from 'react';

const platformFeatures = [
  {
    title: "Pedimentos y expediente digital",
    description: "Consulta cada pedimento con sus facturas, COVE y anexos, con verificación automática de los archivos."
  },
  {
    title: "Transmisión a VUCEM",
    description: "Genera COVE y digitaliza documentos en el formato requerido para obtener tus e-documents."
  },
  {
    title: "Manifestación de valor",
    description: "Captura y transmite tus manifestaciones de valor desde el mismo sistema.",
    link: { href: "/que-es-manifestacion-de-valor-electronica", label: "¿Qué es la MVE?" }
  },
  {
    title: "Control de bodega",
    description: "Registro de entradas y salidas de mercancía con gráficas de seguimiento."
  },
  {
    title: "Envíos automáticos",
    description: "Entrega de expedientes a tus clientes por correo electrónico o SFTP."
  },
  {
    title: "Seguridad y trazabilidad",
    description: "Usuarios con acceso por cliente, bitácora de actividad y consulta de la lista negra del SAT."
  }
];

const platformScreenshots = [
  { src: "/plataforma/dashboard.png", description: "Indicadores clave de tu operación y el historial de transmisiones por mes, en una sola vista.", label: "Panel general", alt: "Panel general de EC Traffic con indicadores y gráfica de transmisiones por mes" },
  { src: "/plataforma/pedimentos.png", description: "Todos tus pedimentos en una lista, con búsqueda por operación, referencia, RFC o razón social.", label: "Pedimentos", alt: "Lista de pedimentos en EC Traffic" },
  { src: "/plataforma/expediente.png", description: "Cada pedimento reúne sus archivos (pedimento, COVE, DODA, acuses y escritos), listos para consultar, descargar o enviar por correo.", label: "Expediente", height: 945, alt: "Detalle de un pedimento en EC Traffic con los archivos de su expediente digital" },
  { src: "/plataforma/conciliacion.png", description: "El sistema revisa que estén todos los COVE y e-documents esperados y te avisa si falta algún archivo.", label: "Conciliación", alt: "Reporte de conciliación de archivos de un pedimento en EC Traffic" },
  { src: "/plataforma/vucem.png", description: "Digitaliza y transmite tus documentos a VUCEM y da seguimiento a cada e-document obtenido.", label: "VUCEM", alt: "Lista de transmisiones VUCEM con sus e-documents en EC Traffic" },
  { src: "/plataforma/manifestaciones.png", description: "Manifestaciones de valor ligadas a su pedimento y COVE, con el estado de cada envío.", label: "Manifestaciones", alt: "Lista de manifestaciones de valor aceptadas en EC Traffic" },
  { src: "/plataforma/bodega.png", description: "Registro detallado de cada entrada de bodega: logística, fechas, mercancías, operadores y sellos.", label: "Bodega", height: 945, alt: "Captura de una entrada de bodega en EC Traffic" },
  { src: "/plataforma/transmisiones.png", description: "Historial completo de transmisiones, con número de operación, e-document y estado de cada una.", label: "Transmisiones", alt: "Lista de transmisiones consultadas en EC Traffic" }
];

const ServicesSection = () => {
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const screenshot = platformScreenshots[activeScreenshot];

  return (
  <section id="servicios" className="w-full py-12 md:py-24 lg:py-32 bg-gray-100 dark:bg-gray-900">
    <div className="container px-4 md:px-6">
      <div className="flex flex-col items-center justify-center space-y-4 text-center">
        <h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-gray-900 dark:text-gray-100">
          Nuestras Soluciones Clave
        </h2>
        <p className="max-w-[700px] text-gray-600 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400">
          Ofrecemos un abanico de servicios especializados para simplificar y potenciar tus operaciones de comercio exterior.
        </p>
      </div>
      <div className="mx-auto grid max-w-5xl items-start gap-8 py-12 sm:grid-cols-2 md:grid-cols-3">
        {[
          {
            title: "Asesoría Aduanal",
            description: "Expertos en regulaciones y procedimientos aduanales para agilizar tus operaciones.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            )
          },
          {
            title: "Anexo 24 y 30",
            description: "Sistemas para el control de inventarios y cumplimiento de obligaciones fiscales.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2"></rect>
                <path d="M3 9h18"></path>
                <path d="M9 21V9"></path>
              </svg>
            )
          },
          {
            title: "Expediente Digital",
            description: "Recuperación y gestión de tu expediente digital conforme a VUCEM.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            )
          },
          {
            title: "Agencia Aduanal",
            description: "Gestión integral de tus operaciones de importación y exportación.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 8v4l3 3"></path>
              </svg>
            )
          },
          {
            title: "Logística y Transporte",
            description: "Coordinación de transporte terrestre, aéreo y marítimo para tus mercancías.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v4"></path>
                <path d="m16 6 3-3"></path>
                <path d="M18 9h4"></path>
                <path d="M17 16.1V22"></path>
                <path d="M21 12h-4"></path>
                <path d="m12 18-3 3"></path>
                <path d="M8 6l-3-3"></path>
                <path d="M6 9H2"></path>
                <path d="M7 16.1V22"></path>
                <path d="M3 12h4"></path>
              </svg>
            )
          },
          {
            title: "Integración de Sistemas",
            description: "Conexión con tus sistemas mediante FTP, APIs o layouts personalizados.",
            icon: (
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m8 3 4 8 5-5 5 15H2L8 3z"></path>
              </svg>
            )
          }
        ].map((service, index) => (
          <div key={index} className="grid gap-2 p-6 rounded-lg border border-gray-300 bg-white shadow-sm transition-all hover:shadow-md dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center gap-4">
              <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-800">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{service.title}</h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400">{service.description}</p>
          </div>
        ))}
      </div>
      <div id="plataforma" className="mx-auto max-w-5xl scroll-mt-20 rounded-lg border border-indigo-200 bg-white p-6 shadow-sm md:p-10 dark:border-indigo-900 dark:bg-gray-950">
        <div className="grid gap-8 lg:grid-cols-5">
          <div className="flex flex-col items-start gap-4 lg:col-span-2">
            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-800 dark:text-indigo-100">
              Nuestro software
            </span>
            <h3 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl dark:text-gray-100">
              EC Traffic
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Un solo sistema en línea para controlar tus operaciones de comercio exterior: pedimentos, documentos y transmisiones a VUCEM, con la información de cada cliente siempre ordenada y disponible.
            </p>
            <a
              href="/#contacto"
              className="inline-flex h-10 items-center justify-center rounded-md bg-indigo-700 px-6 text-sm font-medium text-white shadow transition-colors hover:bg-indigo-800 dark:bg-teal-600 dark:hover:bg-teal-700"
            >
              Solicitar una demostración
            </a>
          </div>
          <ul className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:col-span-3">
            {platformFeatures.map((feature) => (
              <li key={feature.title} className="flex gap-3">
                <svg className="mt-1 h-5 w-5 shrink-0 text-indigo-700 dark:text-teal-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">{feature.title}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{feature.description}</p>
                  {feature.link && (
                    <a href={feature.link.href} className="text-sm font-medium text-indigo-700 hover:underline dark:text-teal-400">
                      {feature.link.label}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-10 border-t border-gray-200 pt-8 dark:border-gray-800">
          <div className="flex flex-wrap justify-center gap-2">
            {platformScreenshots.map((item, index) => (
              <button
                key={item.src}
                type="button"
                aria-pressed={index === activeScreenshot}
                onClick={() => setActiveScreenshot(index)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                  index === activeScreenshot
                    ? "bg-indigo-700 text-white dark:bg-teal-600"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-gray-600 sm:min-h-[2.5rem] dark:text-gray-400">
            {screenshot.description}
          </p>
          <div className="mt-4 overflow-hidden rounded-lg border border-gray-300 shadow-md dark:border-gray-700">
            <img
              src={screenshot.src}
              alt={screenshot.alt}
              width="1421"
              height={screenshot.height || 900}
              loading="lazy"
              className="block h-auto w-full"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

export default ServicesSection;